import { differenceInDays, format, startOfDay } from 'date-fns';
import { DateValue } from '@v2/components/calendar/types';

type OutFmt = 'ymd' | 'dmy' | 'mdy';

function getFormats(outputFormat: OutFmt) {
  switch (outputFormat) {
    case 'ymd':
      // Keep year for both single and ranges so Y-M-D order is preserved
      return { range: 'yyyy-M-d', single: 'yyyy-M-d' };
    case 'dmy':
      return { range: 'dd/MM', single: 'd/M/yyyy' };
    case 'mdy':
    default:
      return { range: 'MM/dd', single: 'M/d/yyyy' };
  }
}

export function splitDatesIntoIntervals(
  dates: DateValue | undefined,
  outputFormat: OutFmt = 'mdy'
): ([string, string] | [string])[] {
  const intervals: ([string, string] | [string])[] = [];
  if (!dates) return [];

  const { range: rangeFmt, single: singleFmt } = getFormats(outputFormat);

  if (dates instanceof Date) {
    return [[format(dates, singleFmt)]];
  }

  if (!Array.isArray(dates)) {
    if (!dates.from || !dates.to) return [];
    return [[format(dates.from, rangeFmt), format(dates.to, rangeFmt)]];
  }

  if (dates.length === 0) return [];
  if (dates.length === 1) return [[format(dates[0], singleFmt)]];

  let start = format(dates[0], rangeFmt);
  let previous = startOfDay(dates[0]);

  for (let i = 1; i < dates.length; i++) {
    const current = startOfDay(dates[i]);

    if (differenceInDays(current, previous) > 1) {
      if (start !== format(previous, rangeFmt)) {
        intervals.push([start, format(previous, rangeFmt)]);
      } else {
        intervals.push([start]);
      }
      start = format(current, rangeFmt);
    }
    previous = current;
  }

  if (start !== format(previous, rangeFmt)) {
    intervals.push([start, format(previous, rangeFmt)]);
  } else {
    intervals.push([start]);
  }

  return intervals;
}
